import React, { useEffect, useState, useReducer } from "react";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, updateDoc, Timestamp } from "firebase/firestore";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import OperatorDashboard from "./pages/OperatorDashboard";
import Kiosk from "./pages/Kiosk";
import Signup from "./pages/Signup";
import { SYSTEM_ADMIN } from "./utils/constants";
import MfaSetup from "./pages/MfaSetup";
import { hasMfa } from "./utils/mfa";

// 2段階認証を「あとで」で先送りできる日数。いきなり必須にすると
// 既存の管理者が締め出されるため、周知のための猶予を置く。
const MFA_GRACE_DAYS = 14;

/** 会社管理者の猶予期限。まだ無ければこの場で決めて記録する。 */
async function ensureGrace(uid, saved) {
  if (saved?.toDate) return saved.toDate();
  const until = new Date(Date.now() + MFA_GRACE_DAYS * 86400000);
  try {
    await updateDoc(doc(db, "users", uid), { mfaGraceUntil: Timestamp.fromDate(until) });
  } catch (e) {
    console.warn("2段階認証の猶予期限を記録できませんでした:", e);
  }
  return until;
}

/**
 * 管理者に2段階認証を求めるかどうか。運営が切り替える。
 * Identity Platform の有効化が済むまでは false のままにしておく。
 * 取得できないときは求めない側に倒す（締め出しを避けるため）。
 */
async function fetchMfaRequired() {
  try {
    const snap = await getDoc(doc(db, "notifySettings", "system"));
    return snap.exists() && snap.data().mfaRequired === true;
  } catch (e) {
    console.warn("2段階認証の設定を取得できませんでした:", e);
    return false;
  }
}

/**
 * 最終ログイン日時を記録する。使われていない会社を運営が把握するために使う。
 * 開くたびに書き込むと無駄なので、同じ端末では1日1回までにしている。
 */
async function recordLogin(uid) {
  try {
    const key = `miyabi_last_login_${uid}`;
    const today = new Date().toDateString();
    if (localStorage.getItem(key) === today) return;
    await updateDoc(doc(db, "users", uid), { lastLoginAt: Timestamp.now() });
    localStorage.setItem(key, today);
  } catch (e) {
    console.warn("最終ログインの記録に失敗しました:", e);
  }
}

function App() {
  const [user, setUser] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [graceUntil, setGraceUntil] = useState(null);
  const [mfaSkipped, setMfaSkipped] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [, refreshMfa] = useReducer(x => x + 1, 0);

  const params = new URLSearchParams(window.location.search);
  const kioskToken = params.get("kiosk");
  const isSignup = params.get("signup") === "1" || window.location.pathname === "/signup";

  useEffect(() => {
    if (kioskToken) return;
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      if (u) {
        setUser(u);
        if (u.email === SYSTEM_ADMIN) {
          setUserStatus("approved");
          setLoading(false);
          return;
        }
        const snap = await getDoc(doc(db, "users", u.uid));
        if (snap.exists()) {
          const data = snap.data();
          setUserStatus(data.status);
          setPermissions(data.permissions || {});
          setCompanyId(data.companyId || null);
          recordLogin(u.uid);
          if (data.permissions?.companyAdmin === true) {
            setMfaRequired(await fetchMfaRequired());
            setGraceUntil(await ensureGrace(u.uid, data.mfaGraceUntil));
          }
        } else {
          setUserStatus("no_company");
        }
      } else {
        // ログアウト（user→null）のときだけリロードして自動入力キャッシュをクリアする。
        // 初回ロード時は user state が null のままなのでリロードしない。
        setUser(prev => {
          if (prev !== null) window.location.reload();
          return null;
        });
        setUserStatus(null);
        setCompanyId(null);
        setPermissions({});
        setMfaSkipped(false);
        setMfaRequired(false);
        setGraceUntil(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [kioskToken]);

  if (kioskToken) return <Kiosk token={kioskToken} />;
  if (isSignup) return <Signup />;
  if (loading) return <div style={{ textAlign: "center", marginTop: "100px" }}>読み込み中...</div>;
  if (!user) return <Login />;

  if (userStatus === "pending") {
    return (
      <div style={s.center}>
        <div style={s.box}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>⏳</div>
          <h2 style={{ color: "#2c3e50" }}>承認待ちです</h2>
          <p style={{ color: "#7f8c8d", lineHeight: "1.7" }}>管理者が承認するまでお待ちください。</p>
          <button onClick={() => auth.signOut()} style={s.btn}>ログアウト</button>
        </div>
      </div>
    );
  }

  if (userStatus === "no_company") {
    return (
      <div style={s.center}>
        <div style={s.box}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🏢</div>
          <h2 style={{ color: "#2c3e50" }}>会社が登録されていません</h2>
          <p style={{ color: "#7f8c8d" }}>管理者にお問い合わせください。</p>
          <button onClick={() => auth.signOut()} style={s.btn}>ログアウト</button>
        </div>
      </div>
    );
  }

  // 給与や従業員情報を扱う会社管理者は2段階認証を必須にする。
  // 猶予期間のあいだは「あとで設定する」を選べる。
  // 運営は対象外。PINによる裏口を廃止し、メールとパスワードのみに絞ったうえでの判断。
  const isAdminAccount = permissions?.companyAdmin === true;
  if (mfaRequired && isAdminAccount && !hasMfa(user) && !mfaSkipped) {
    return (
      <MfaSetup
        user={user}
        canSkip={!!graceUntil && Date.now() < graceUntil.getTime()}
        graceUntil={graceUntil}
        onSkip={() => setMfaSkipped(true)}
        onDone={async () => {
          try { await auth.currentUser?.reload(); } catch { /* 反映は次回ログインでもよい */ }
          refreshMfa();
        }}
      />
    );
  }

  // システム管理者（運営）は専用ダッシュボードへ
  if (user.email === SYSTEM_ADMIN) return <OperatorDashboard user={user} />;

  return <Dashboard user={user} permissions={permissions} companyId={companyId} isAdmin={permissions?.companyAdmin === true} />;
}

const s = {
  center: { display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", backgroundColor: "#f0f4f8" },
  box: { backgroundColor: "#fff", padding: "40px", borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.1)", width: "360px", textAlign: "center" },
  btn: { marginTop: "24px", padding: "10px 24px", backgroundColor: "#7f8c8d", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer" },
};

export default App;
